import {
	Grid,
	Input,
	LoadingOverlay,
	Modal,
	PasswordInput,
	Space,
	Text,
	TextInput,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IMaskInput as Mask } from 'react-imask';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import type { OrganisationDto } from '../../../util/types/OrganisationDto';
import { useAddOrganisationForm } from '../hooks/useAddOrganisationForm';
import { useServerOrganisationValidation } from '../hooks/useServerOrganisationValidation';
import type { NewOrganisationDto } from '../types/NewOrganisationDto';
import { FormButtons } from '../../../components/form/FormButtons';

interface OrganisationEditorModal {
	close: () => void;
	opened: boolean;
	triggerListUpdate: () => void;
}

export function NewOrganisationModal({
	opened,
	close,
	triggerListUpdate,
}: OrganisationEditorModal) {
	const successModal = useModal();

	const { form, inputs } = useAddOrganisationForm();

	const newOrganisationMutation = mutate<NewOrganisationDto, OrganisationDto>(
		'organisation/add',
		inputs,
	);

	useServerOrganisationValidation(newOrganisationMutation, form);

	const resetAndClose = () => {
		form.reset();
		triggerListUpdate();
		close();
		successModal.close();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={close}
				title={<Title label={'New Organisation'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newOrganisationMutation.isLoading} />
				<SmallModal
					close={resetAndClose}
					isOpen={successModal.isOpen}
					title={'Organisation Added!'}
					body={'A new organisation has been added. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newOrganisationMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<Grid>
						<OrganisationInputs form={form} />
						<FirstUserInputs form={form} />
					</Grid>
					<Space h={20} />
					<FormButtons close={close} />
				</form>
			</Modal>
		</>
	);
}

export function FirstUserInputs({
	form,
}: {
	form: UseFormReturnType<
		NewOrganisationDto,
		(values: NewOrganisationDto) => NewOrganisationDto
	>;
}) {
	return (
		<>
			<Text bg={'transparent'} pl={'9'} c={'grey'} size={'0.75rem'}>
				Login information will be used to create the initial sign-in details for
				the admin of the organisation.
			</Text>
			<Grid.Col span={12}>
				<TextInput
					label={'Username'}
					placeholder={'Insert username...'}
					key={form.key('username')}
					{...form.getInputProps('username')}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<PasswordInput
					label={'Password'}
					placeholder={'Insert password...'}
					key={form.key('password')}
					{...form.getInputProps('password')}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<PasswordInput
					label={'Confirm Password'}
					placeholder={'Check password matches...'}
					key={form.key('checkPassword')}
					{...form.getInputProps('checkPassword')}
				/>
			</Grid.Col>
		</>
	);
}

export function OrganisationInputs<T>({
	form,
}: {
	form: UseFormReturnType<T, (values: T) => T>;
}) {
	return (
		<>
			<Grid.Col>
				<TextInput
					label={'Organisation Name'}
					placeholder={'Insert name of new organisation...'}
					key={form.key('name')}
					{...form.getInputProps('name')}
				/>
			</Grid.Col>
			<Grid.Col>
				<TextInput
					label={'Connection String'}
					placeholder={
						'Insert connection string of database associated with organisation...'
					}
					key={form.key('connectionString')}
					{...form.getInputProps('connectionString')}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<TextInput
					label={'Email'}
					placeholder={'Insert email...'}
					key={form.key('email')}
					{...form.getInputProps('email')}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<Input.Wrapper
					label={'Phone Number'}
					key={form.key('phoneNumber')}
					{...form.getInputProps('phoneNumber')}
				>
					<Input
						component={Mask}
						mask={'00000 000000'}
						placeholder={'Insert phone number...'}
						key={form.key('phoneNumber')}
						{...form.getInputProps('phoneNumber')}
					/>
				</Input.Wrapper>
			</Grid.Col>
		</>
	);
}
